import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Cable, Cloud, Globe, Menu, PackageSearch, ShoppingCart, X } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { scrollToId } from "@/lib/scroll";
import { api, imgUrl } from "@/lib/api";
import { useLang } from "@/i18n";

const SECTIONS = [
  { id: "home", label: "nav_home" },
  { id: "about", label: "nav_about" },
  { id: "services", label: "nav_services" },
  { id: "bitdefender", label: "nav_bitdefender" },
  { id: "projects", label: "nav_projects" },
  { id: "team", label: "nav_team" },
  { id: "contact", label: "nav_contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [logo, setLogo] = useState("");
  const [hasComingSoonApp, setHasComingSoonApp] = useState(false);
  const { count, setOpen } = useCart();
  const { t, lang, setLang } = useLang();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    api.get("/settings/public").then((r) => setLogo(r.data?.site_logo || "")).catch(() => {});
    api.get("/apps").then((r) => setHasComingSoonApp((r.data || []).some((a) => a.status !== "live"))).catch(() => {});
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goSection = (id) => {
    setMenuOpen(false);
    if (location.pathname === "/") {
      scrollToId(id);
    } else {
      navigate("/", { state: { scrollTo: id } });
    }
  };

  return (
    <header
      data-testid="main-navbar"
      style={{ top: "var(--announce-h, 0px)" }}
      className={`fixed left-0 right-0 z-40 bg-white border-b border-slate-200 transition-[padding,box-shadow] duration-500 ${
        scrolled ? "py-2.5 shadow-[0_4px_24px_rgba(11,17,32,0.08)]" : "py-4"
      }`}
    >
      <div className="px-6 md:px-12 lg:px-24 flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => goSection("home")}
          className="flex items-center gap-3 group"
        >
          {logo ? (
            <img src={imgUrl(logo)} alt="KINOS Leading Innovation Technology" className="h-10 w-auto object-contain" />
          ) : (
            <>
              <span className="w-9 h-9 bg-[#ED1C24] flex items-center justify-center font-display font-bold text-white text-lg -skew-x-6 group-hover:skew-x-0 transition-transform duration-300">
                K
              </span>
              <span className="font-display font-semibold text-lg tracking-tight text-[#0B1120]">
                KINOS
                <span className="text-[#ED1C24]">.</span>
                <span className="hidden sm:inline text-[10px] font-sans font-light text-slate-500 ml-2 tracking-[0.25em] uppercase">
                  Leading Innovation
                </span>
              </span>
            </>
          )}
        </button>

        <nav className="hidden lg:flex items-center gap-5 xl:gap-7">
          {SECTIONS.map((s) => (
            <button
              key={s.id}
              data-testid={`nav-link-${s.id}`}
              onClick={() => goSection(s.id)}
              className="text-base text-slate-600 hover:text-[#ED1C24] tracking-wide transition-colors duration-300 relative after:absolute after:left-0 after:-bottom-1 after:h-px after:w-0 after:bg-[#ED1C24] after:transition-[width] after:duration-300 hover:after:w-full"
            >
              {t(s.label)}
            </button>
          ))}
          <button
            data-testid="nav-link-shop"
            onClick={() => navigate("/shop")}
            className="text-base text-[#ED1C24] hover:text-[#C81016] font-medium tracking-wide transition-colors duration-300"
          >
            {t("nav_shop")}
          </button>
          <button
            data-testid="nav-link-apps"
            onClick={() => navigate("/apps")}
            className="text-base text-slate-600 hover:text-[#ED1C24] tracking-wide transition-colors duration-300 relative after:absolute after:left-0 after:-bottom-1 after:h-px after:w-0 after:bg-[#ED1C24] after:transition-[width] after:duration-300 hover:after:w-full"
          >
            {t("nav_apps")}
            {hasComingSoonApp && (
              <span
                data-testid="nav-apps-new-badge"
                className="absolute -top-2 -right-7 rounded-full bg-[#ED1C24] text-white text-[9px] font-semibold px-1.5 py-px leading-tight uppercase tracking-wide"
              >
                {t("nav_apps_new")}
              </span>
            )}
          </button>
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="language-switcher"
            onClick={() => setLang(lang === "en" ? "tet" : "en")}
            className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 hover:border-[#ED1C24] text-slate-600 hover:text-[#ED1C24] text-xs font-medium px-3.5 py-2 transition-colors duration-300"
            aria-label="Switch language"
          >
            <Globe size={13} />
            {lang === "en" ? "EN" : "TT"}
          </button>
          <button
            data-testid="cart-button"
            onClick={() => setOpen(true)}
            className="relative w-10 h-10 rounded-full border border-slate-300 flex items-center justify-center text-slate-700 hover:border-[#ED1C24] hover:text-[#ED1C24] transition-colors duration-300"
            aria-label="Open cart"
          >
            <ShoppingCart size={17} />
            {count > 0 && (
              <span
                data-testid="cart-count-badge"
                className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-[#ED1C24] text-white text-[10px] font-semibold flex items-center justify-center px-1"
              >
                {count}
              </span>
            )}
          </button>
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setMenuOpen((v) => !v)}
            className="lg:hidden w-10 h-10 rounded-full border border-slate-300 flex items-center justify-center text-slate-700"
            aria-label="Toggle menu"
          >
            {menuOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35 }}
            className="lg:hidden overflow-hidden bg-white border-b border-slate-200"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {[...SECTIONS, { id: "shop", label: "nav_shop" }, { id: "apps", label: "nav_apps" }, { id: "downloads", label: "nav_catalogue" }, { id: "cctv-kit-builder", label: "nav_cctv_kit" }, { id: "support", label: "support_label" }, { id: "status", label: "nav_status" }].map((s) => (
                <button
                  key={s.id}
                  data-testid={`mobile-nav-${s.id}`}
                  onClick={() => {
                    if (["shop", "downloads", "support", "status", "cctv-kit-builder", "apps"].includes(s.id)) {
                      setMenuOpen(false);
                      navigate(`/${s.id}`);
                    } else {
                      goSection(s.id);
                    }
                  }}
                  className="text-left font-display text-2xl text-slate-800 hover:text-[#ED1C24] transition-colors"
                >
                  {t(s.label)}
                </button>
              ))}

              <div className="grid grid-cols-2 gap-3 pt-5 mt-1 border-t border-slate-200">
                <button
                  data-testid="mobile-nav-tracking"
                  onClick={() => { setMenuOpen(false); navigate("/tracking"); }}
                  className="group flex flex-col items-start gap-2.5 border border-[#ED1C24]/30 bg-[#ED1C24]/5 hover:bg-[#ED1C24] p-4 text-left transition-colors duration-300"
                >
                  <PackageSearch size={22} className="text-[#ED1C24] group-hover:text-white transition-colors duration-300" />
                  <span className="font-display font-semibold text-base leading-snug text-[#0B1120] group-hover:text-white transition-colors duration-300">
                    {t("nav_track_mobile")}
                  </span>
                </button>
                <button
                  data-testid="mobile-nav-cloud"
                  onClick={() => { setMenuOpen(false); navigate("/cloud"); }}
                  className="group flex flex-col items-start gap-2.5 border border-[#0B1120]/20 bg-[#0B1120]/5 hover:bg-[#0B1120] p-4 text-left transition-colors duration-300"
                >
                  <Cloud size={22} className="text-[#0B1120] group-hover:text-white transition-colors duration-300" />
                  <span className="font-display font-semibold text-base leading-snug text-[#0B1120] group-hover:text-white transition-colors duration-300">
                    {t("cloud_label")}
                  </span>
                </button>
                <button
                  data-testid="mobile-nav-kit"
                  onClick={() => { setMenuOpen(false); navigate("/kit-builder"); }}
                  className="group col-span-2 flex items-center gap-3 border border-[#FFC61A]/40 bg-[#FFC61A]/10 hover:bg-[#FFC61A] p-4 text-left transition-colors duration-300"
                >
                  <Cable size={22} className="text-[#B8860B] group-hover:text-[#0B1120] transition-colors duration-300" />
                  <span className="font-display font-semibold text-base leading-snug text-[#0B1120]">
                    {t("nav_kit")} — {t("kitcta_label")}
                  </span>
                </button>
              </div>

              <button
                data-testid="mobile-language-switcher"
                onClick={() => setLang(lang === "en" ? "tet" : "en")}
                className="text-left font-display text-2xl text-[#ED1C24] hover:text-[#C81016] transition-colors inline-flex items-center gap-3"
              >
                <Globe size={20} /> {t("lang_name")}
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
